import React, {createContext, useContext, useMemo} from 'react';

import {
  type ContextProviderState,
  type MediaSettingsActionsContextValue,
  type MediaSettingsStateContextValue,
} from '../types';

/**
 * =============================================================================
 * MEDIA SETTINGS CONTEXT - POLYMORPHIC ARCHITECTURE
 * =============================================================================
 *
 * This file provides a shared context system for media settings across three
 * different settings levels: PANEL, SECTION, and WORKSPACE. The same ~40
 * consumer components (YAxis, XAxis, NumberOfColumns, etc.) are reused across
 * all levels without knowing which level they're rendered in.
 *
 * POLYMORPHIC PATTERN:
 * --------------------
 * The context is "polymorphic" because the same context interface is used
 * regardless of the settings level. The level-specific wrapper providers
 * inject the appropriate values:
 *
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │  PANEL LEVEL (MediaPanelSettingsContextProvider)                    │
 *   │    └── MediaSettingsContextProvider                                 │
 *   │        - config comes from panel's config                           │
 *   │        - updateConfig updates the panel                             │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │  SECTION LEVEL (MediaSectionSettingsContextProvider)                │
 *   │    └── MediaSettingsContextProvider                                 │
 *   │        - config comes from section's media settings                 │
 *   │        - updateConfig updates the section                           │
 *   │        - includes panel override counts/clearing                    │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │  WORKSPACE LEVEL (MediaWorkspaceSettingsContextProvider)            │
 *   │    └── MediaSettingsContextProvider                                 │
 *   │        - config comes from workspace's media settings               │
 *   │        - updateConfig updates the workspace                         │
 *   │        - includes section & panel override counts/clearing          │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * PERFORMANCE OPTIMIZATION - SPLIT CONTEXTS:
 * -------------------------------------------
 * To prevent unnecessary re-renders, the context is split into two:
 *
 * 1. Actions Context (stable): Callbacks and static values that rarely change
 *    - updateConfig, unsetConfigField, clearOverridesBelow
 *    - settingsLevel, rampFlags
 *
 * 2. State Context (dynamic): Values that change during user interaction
 *    - config, configWithoutDefaults, loading
 *    - hasMasks, hasBoundingBoxes, allBoxes, allMasks, sliderRanges
 *    - mediaCardTypes, numberOfOverridesBelow
 *
 * Components can subscribe to only what they need:
 * - useMediaSettingsActions() - Won't re-render on state changes
 * - useMediaSettingsState() - Re-renders when state changes
 * - useMediaSettingsContext() - Subscribes to both (primary hook)
 *
 * SPECIAL CASE - WORKSPACE ACTIONS:
 * ----------------------------------
 * Some settings (like video playback sync) always update at the workspace level,
 * even when displayed in panel/section drawers. For these cases, import and use:
 * - useWorkspaceMediaSettingsConfig() from MediaWorkspaceSettingsContextProvider
 */

const MediaSettingsActionsContext =
  createContext<MediaSettingsActionsContextValue | null>(null);

const MediaSettingsStateContext =
  createContext<MediaSettingsStateContextValue | null>(null);

/**
 * Polymorphic base provider that splits the context value into actions and state contexts.
 *
 * This provider is "polymorphic" because it accepts pre-computed context values from
 * level-specific wrapper providers. The same context interface is used regardless of
 * whether the settings are for a panel, section, or workspace - only the injected
 * values differ.
 *
 * @internal This provider should not be used directly in drawer components.
 * Use the level-specific wrapper providers instead:
 * - MediaPanelSettingsContextProvider (panel level)
 * - MediaSectionSettingsContextProvider (section level)
 * - MediaWorkspaceSettingsContextProvider (workspace level)
 */
export function MediaSettingsContextProvider({
  children,
  contextValue,
}: {
  children: React.ReactNode;
  contextValue: ContextProviderState;
}) {
  // Split into stable actions (callbacks + static values)
  const actionsValue = useMemo<MediaSettingsActionsContextValue>(
    () => ({
      clearOverridesBelow: contextValue.clearOverridesBelow,
      panelRef: contextValue.panelRef,
      rampFlags: contextValue.rampFlags,
      settingsLevel: contextValue.settingsLevel,
      unsetConfigField: contextValue.unsetConfigField,
      unsetConfigFields: contextValue.unsetConfigFields,
      updateConfig: contextValue.updateConfig,
    }),
    [
      contextValue.clearOverridesBelow,
      contextValue.panelRef,
      contextValue.rampFlags,
      contextValue.settingsLevel,
      contextValue.unsetConfigField,
      contextValue.unsetConfigFields,
      contextValue.updateConfig,
    ]
  );

  // Split into dynamic state values
  const stateValue = useMemo<MediaSettingsStateContextValue>(
    () => ({
      allBoxes: contextValue.allBoxes,
      allMasks: contextValue.allMasks,
      config: contextValue.config,
      configWithoutDefaults: contextValue.configWithoutDefaults,
      hasBoundingBoxes: contextValue.hasBoundingBoxes,
      hasMasks: contextValue.hasMasks,
      loading: contextValue.loading,
      mediaCardTypes: contextValue.mediaCardTypes,
      numberOfOverridesBelow: contextValue.numberOfOverridesBelow,
      sliderRanges: contextValue.sliderRanges,
    }),
    [
      contextValue.allBoxes,
      contextValue.allMasks,
      contextValue.config,
      contextValue.configWithoutDefaults,
      contextValue.hasBoundingBoxes,
      contextValue.hasMasks,
      contextValue.loading,
      contextValue.mediaCardTypes,
      contextValue.numberOfOverridesBelow,
      contextValue.sliderRanges,
    ]
  );

  return (
    <MediaSettingsActionsContext.Provider value={actionsValue}>
      <MediaSettingsStateContext.Provider value={stateValue}>
        {children}
      </MediaSettingsStateContext.Provider>
    </MediaSettingsActionsContext.Provider>
  );
}

/**
 * Hook to access stable action callbacks and static values.
 *
 * USE THIS HOOK when your component only needs:
 * - updateConfig, unsetConfigField, clearOverridesBelow (callbacks)
 * - settingsLevel, rampFlags (static values)
 *
 * BENEFIT: Components using only this hook won't re-render when
 * dynamic state changes (config, loading, allBoxes, etc.).
 */
export function useMediaSettingsActions(): MediaSettingsActionsContextValue {
  const context = useContext(MediaSettingsActionsContext);
  if (context === null) {
    throw new Error(
      'useMediaSettingsActions must be used within a MediaSettingsContextProvider'
    );
  }
  return context;
}

/**
 * Hook to access dynamic state values.
 *
 * USE THIS HOOK when your component needs:
 * - config, configWithoutDefaults (current settings)
 * - loading, hasMasks, hasBoundingBoxes (derived state)
 * - allBoxes, allMasks, sliderRanges, mediaCardTypes (data)
 * - numberOfOverridesBelow (override counts)
 *
 * NOTE: Components using this hook WILL re-render when any state changes.
 */
export function useMediaSettingsState(): MediaSettingsStateContextValue {
  const context = useContext(MediaSettingsStateContext);
  if (context === null) {
    throw new Error(
      'useMediaSettingsState must be used within a MediaSettingsContextProvider'
    );
  }
  return context;
}

/**
 * Combined hook that provides all context values (actions + state).
 *
 * This hook subscribes to BOTH contexts, so components using it will
 * re-render when either actions OR state changes. This is the same
 * behavior as before the context split.
 *
 * For better performance in specific components, consider using:
 * - useMediaSettingsActions() if you only need callbacks
 * - useMediaSettingsState() if you only need state
 */
export function useMediaSettingsContext(): ContextProviderState {
  const actions = useMediaSettingsActions();
  const state = useMediaSettingsState();
  return {...actions, ...state};
}
